import { combineReducers } from 'redux'
import {
  ApiCallState,
  callFailed,
  callInitiated,
  callSucceeded
} from '../../types'
import { defaultFilter, Filter } from '../filter/types'
import { composeReducers, uniq, unknownErrorToString } from '../helpers'
import { Order, OrderType } from '../order/types'
import {
  AddOrUpdateListTradingSecuritiesAction,
  GridSpecificAction,
  GridViewStateAction,
  ListTradingActions,
  SecuritiesAction
} from './actions'
import {
  insertGridDef,
  ReduceSecurityStaticData,
  removeGridDef,
  securityOrderDataFromSecurity
} from './helpers'
import { QuoteReliability } from './types'

export interface Security {
  id: number
  isRestricted: boolean
  issuerSymbol: string
  cusip: string
  isin: string
  coupon: number
  couponType: string
  maturityDate: Date
  currency: string
  dayCount: string
  debtType: string
  minimumSize: number
  minimumSizeIncrement?: number
  defaultOrderSize?: number
  minimumPriceIncrement?: number
  benchmarkName?: string
  bidBenchmarkPrice: string
  offerBenchmarkPrice: string
  bestBid?: Order
  bestOffer?: Order
  eligibleRfqBidCount: number
  eligibleRfqOfferCount: number
  amountIssued: number
  amountOutstanding: number
  datedDate: Date
  benchmarkISIN: string
  benchmarkCusip: string
  benchmarkSecurity: string
  isBenchmark: boolean
  benchmarkMaturity?: Date
  benchmarkCoupon?: number
  country: string
  firstCouponDate?: Date
  nextCouponDate?: Date
  issueDate: Date
  markupRate: number
  payFreq: string
  product?: string
  settlementDate: Date
  sAndPRating: string
  sector: string
  termToMat: string
  minimumPiece: number
  description: string
  boardLabel: string
  series: string
  issuerName: string
  marketVolume: number
  internalVolume: number
  admin7Chord?: Security7ChordData
  bestRfqBid?: Order
  bestRfqOffer?: Order
}

export interface BidOffer7Chord {
  price_7Chord: number
  dateTime_7Chord: Date
  oasSpread_7Chord: number
  tSpread_7Chord: number
  ytm_7Chord: number
  dayOverSignal_7Chord: number
  signalOverSignal_7Chord: number
  lastClose_7Chord: number
}

export interface SecurityStaticData {
  id: number
  isRestricted: boolean
  issuerSymbol: string
  cusip: string
  isin: string
  coupon: number
  couponType: string
  maturityDate: Date
  currency: string
  dayCount: string
  debtType: string
  minimumSize: number
  minimumSizeIncrement?: number
  defaultOrderSize?: number
  minimumPriceIncrement?: number
  benchmarkName?: string
  bidBenchmarkPrice: string
  offerBenchmarkPrice: string
  amountIssued: number
  amountOutstanding: number
  datedDate: Date
  benchmarkISIN: string
  benchmarkCusip: string
  benchmarkSecurity: string
  isBenchmark: boolean
  benchmarkMaturity?: Date
  benchmarkCoupon?: number
  country: string
  firstCouponDate?: Date
  nextCouponDate?: Date
  issueDate: Date
  markupRate: number
  payFreq: string
  product?: string
  settlementDate: Date
  sAndPRating: string
  sector: string
  termToMat: string
  minimumPiece: number
  description: string
  boardLabel: string
  series: string
  issuerName: string
  marketVolume: number
  internalVolume: number
}

export interface Security7ChordData {
  id: number
  averageDailyTradeCount_7Chord?: number
  timesTraded_7Chord?: number
  timeSinceLastTrade_7Chord?: number
  topExpertPrediction_7Chord?: number
  bid?: BidOffer7Chord
  ofr?: BidOffer7Chord
}

export interface SecurityOrderData {
  id: number
  isRestricted: boolean
  bestBid?: Order
  bestOffer?: Order
  isin?: Security['isin']
  marketVolume: number
  isBenchmark: boolean
  eligibleRfqBidCount: number
  eligibleRfqOfferCount: number
  bestRfqBid?: Order
  bestRfqOffer?: Order
}

export interface GridDefinition {
  watchlistId: number | undefined
  issuerFilter?: string
  securitiesFilter?: Array<Security['id']>
  queryFilter?: string
  isMine: boolean
  showLive: boolean
  showRfqEligible: boolean
  myFirm: boolean
  myBook: boolean
  quoteReliability: QuoteReliability
  order: number
  sortToTop: number[]
  filter: Filter
  size: number
  useAdvancedFilter: boolean
  selectedSecurityId?: Security['id']
}

export type PageUpdate = {
  type: 'add'
  securityId: number
  nextSecurityId: number
}

export type RfqDirectionState = {
  isVisible: boolean
  orderId?: Order['id']
}
export type RfqOrderDisplayState = {
  [key in OrderType]?: RfqDirectionState
}

export interface GridData {
  currentPage?: number
  pages: Record<
    number,
    {
      status: 'loading' | 'loaded'
      securityIds: Array<Security['id']>
    }
  >
  pending: boolean
  error: boolean
  pageUpdates: PageUpdate[]
  rfqState: Record<Security['id'], RfqOrderDisplayState>
}

export interface GridViewState {
  callState: ApiCallState
  securityIds: Array<Security['id']> | null
  rfqState: Record<Security['id'], RfqOrderDisplayState>
}

export const getEmptyGridView = (): GridViewState => ({
  callState: { ...callInitiated },
  securityIds: null,
  rfqState: {}
})

export interface State {
  gridDefinitions: { [gridIndex: number]: GridDefinition }
  gridViewState: { [gridIndex: number]: GridViewState }
  securitiesOrderDataInfoCache: Record<Security['id'], SecurityOrderData>
  securitiesStaticDataInfoCache: Record<Security['id'], SecurityStaticData>
  securities7ChordDataInfoCache: Record<Security['id'], Security7ChordData>
  listTradingSecurities: GridData
  securityExportData: {
    gridIndex?: number
    includeMakerName?: boolean
    callState: ApiCallState
    securities?: Security[]
  }
  allSecuritiesLoadState: ApiCallState & {
    loaded: boolean // allows us to know when cache is legitimately empty
  }
}

interface GridDefinitionsState {
  [gridIndex: number]: GridDefinition
}

type SecuritiesInfoCacheState = Record<
  SecurityOrderData['id'],
  SecurityOrderData
>
type SecuritiesStaticInfoCacheState = Record<
  SecurityStaticData['id'],
  SecurityStaticData
>

type Securities7ChordInfoCacheState = Record<
  Security7ChordData['id'],
  Security7ChordData
>

const initialGridDefinitions: GridDefinitionsState = {
  /* 0: {
    watchlistId: undefined,
    isMine: false,
    showLive: false,
    quoteReliability: QuoteReliability.All,
    order: 0
  }*/
}

const defaultGridData: GridData = {
  currentPage: undefined,
  pages: {},
  pending: false,
  error: false,
  pageUpdates: [],
  rfqState: {}
}

const initialSecuritiesOrderDataInfoCache: SecuritiesInfoCacheState = {}
const initialSecuritiesStaticDataInfoCache: SecuritiesStaticInfoCacheState = {}
const initialSecurities7ChordDataInfoCache: Securities7ChordInfoCacheState = {}

const updateGridDataWithState = <T>(
  gridData: { [gridIndex: number]: T },
  gridIndex: number,
  updatedData: Partial<T>
): { [gridIndex: number]: T } => ({
  ...gridData,
  [gridIndex]: {
    ...gridData[gridIndex],
    ...updatedData
  }
})

const gridDataMiniReducer = (
  gridData = defaultGridData,
  action: GridSpecificAction | AddOrUpdateListTradingSecuritiesAction
): GridData => {
  switch (action.type) {
    case 'securities.addOrUpdateSecurities':
    case 'securities.addOrUpdateListTradingSecurities':
      return {
        ...gridData,
        pending: false,
        error: false,
        pages: {
          ...gridData.pages,
          [action.payload.page]: {
            status: 'loaded',
            securityIds: uniq([
              ...(gridData.pages[action.payload.page]?.securityIds || []),
              ...action.payload.securities.map((security) => security.id)
            ])
          }
        }
      }

    case 'securities.insertSecurity':
      return {
        ...gridData,
        pageUpdates: [
          ...gridData.pageUpdates.filter(
            (u) => u.securityId !== action.payload.securityId
          ),
          { ...action.payload, type: 'add' }
        ]
      }
    case 'securities.processInsertSecurity': {
      const gridPages = { ...gridData.pages }
      const { nextSecurityId, securityId } = action.payload.update
      for (const [pageNum, pageData] of Object.entries(gridData.pages)) {
        const insertionPoint = pageData.securityIds.findIndex(
          (id) => id === nextSecurityId
        )
        if (insertionPoint > -1) {
          const securityIds = [
            ...pageData.securityIds.slice(0, insertionPoint),
            securityId,
            ...pageData.securityIds.slice(insertionPoint)
          ]
          gridPages[Number(pageNum)].securityIds = securityIds
        }
      }
      return {
        ...gridData,
        pages: gridPages,
        pageUpdates: gridData.pageUpdates.filter(
          (u) => u.securityId !== securityId
        )
      }
    }
    case 'securities.setRfqEntryVisible': {
      const { securityId, type, isVisible } = action.payload

      const securityRfqState = gridData.rfqState[securityId] || {}
      const rfqState = {
        ...gridData.rfqState,
        [securityId]: {
          ...securityRfqState,
          [type]: { isVisible }
        }
      }
      return {
        ...gridData,
        rfqState
      }
    }
    case 'securities.setRfqEntryOrderId': {
      const { securityId, orderId, type } = action.payload
      const securityRfqState = gridData.rfqState[securityId] || {}
      const directionState: Partial<RfqDirectionState> =
        securityRfqState[type] || {}
      const rfqState = {
        ...gridData.rfqState,
        [securityId]: {
          ...securityRfqState,
          [type]: { ...directionState, isVisible: true, orderId }
        }
      }
      return {
        ...gridData,
        rfqState
      }
    }
    case 'securities.setWatchListId':
    case 'securities.setIssuerFilter':
    case 'securities.setSecuritiesFilter':
    case 'securities.setQueryFilter':
    case 'securities.sortValidationsToTopAction':
    case 'securities.saveAdvancedFilter':
    case 'securities.setIsMine':
    case 'securities.setUseAdvancedFilter':
    case 'securities.setShowLive':
    case 'securities.setShowRfqEligible':
    case 'securities.setQuoteReliability':
    case 'securities.applyQuickFilters':
      return {
        ...defaultGridData
      }
    default:
      return gridData
  }
}

const listTradingReducer = (
  gridData = { ...defaultGridData },
  action: ListTradingActions
) => {
  switch (action.type) {
    case `securities.fetchListTradingSecurities`:
      return {
        ...defaultGridData,
        pending: true,
        pages: {
          [action.payload.page]: {
            status: 'loading' as const,
            securityIds: [] as Array<Security['id']>
          }
        }
      }
    case `securities.addOrUpdateListTradingSecurities`:
      return {
        ...gridDataMiniReducer(gridData, action),
        currentPage: action.payload.page
      }
    case `securities.fetchListTradingSecuritiesFailure`:
      return { ...gridData, pending: false, error: true }
    case 'securities.unsubscribeListTrading':
      return { ...defaultGridData }
    default:
      return gridData
  }
}

const gridViewStateReducer = (
  state: State['gridViewState'] = {},
  action: GridViewStateAction
): State['gridViewState'] => {
  switch (action.type) {
    case 'securities.fetchGridViewState':
      return {
        ...state,
        [action.payload]: getEmptyGridView()
      }
    case 'securities.fetchGridViewStateSucceeded': {
      const { gridIndex, securityIds } = action.payload
      const thisState = state[gridIndex]
      const newSecurities = (thisState.securityIds ?? []).concat(
        securityIds.filter((id) => !thisState.securityIds?.includes(id))
      )
      return {
        ...state,
        [gridIndex]: {
          ...thisState,
          securityIds: newSecurities,
          callState: callSucceeded
        }
      }
    }
    case 'securities.fetchGridViewStateFailed': {
      const { gridIndex, error } = action.payload
      const thisState = state[gridIndex]
      return {
        ...state,
        [gridIndex]: {
          ...thisState,
          callState: {
            ...callFailed,
            status: unknownErrorToString(error)
          }
        }
      }
    }
    case 'securities.setRfqEntryVisible': {
      const { gridIndex, securityId, type, isVisible } = action.payload
      const thisState = state[gridIndex] || getEmptyGridView()
      const securityRfqState = thisState.rfqState[securityId] || {}
      return {
        ...state,
        [gridIndex]: {
          ...thisState,
          rfqState: {
            ...thisState.rfqState,
            [securityId]: {
              ...securityRfqState,
              [type]: { isVisible }
            }
          }
        }
      }
    }
    case 'securities.setRfqEntryOrderId': {
      const { gridIndex, securityId, orderId, type } = action.payload
      const thisState = state[gridIndex] || getEmptyGridView()
      const securityRfqState = thisState.rfqState[securityId] || {}
      const directionState: Partial<RfqDirectionState> =
        securityRfqState[type] || {}
      return {
        ...state,
        [gridIndex]: {
          ...thisState,
          rfqState: {
            ...thisState.rfqState,
            [securityId]: {
              ...securityRfqState,
              [type]: { ...directionState, isVisible: true, orderId }
            }
          }
        }
      }
    }
    case 'securities.applyQuickFilters': {
      const { gridIndex } = action.payload
      return {
        ...state,
        [gridIndex]: getEmptyGridView()
      }
    }
  }
  return state
}

const gridDefinitionsMiniReducer = (
  gridDefinition: GridDefinition,
  action: GridSpecificAction
): GridDefinition => {
  switch (action.type) {
    case 'securities.setWatchListId':
      if (
        gridDefinition.watchlistId === action.payload.watchlistId &&
        gridDefinition.securitiesFilter === undefined
      ) {
        return gridDefinition
      }
      return {
        ...gridDefinition,
        watchlistId: action.payload.watchlistId,
        securitiesFilter: undefined
      }
    case 'securities.setIssuerFilter':
      return {
        ...gridDefinition,
        issuerFilter: action.payload.issuerFilter,
        queryFilter: undefined,
        securitiesFilter: action.payload.issuerFilter
          ? undefined
          : gridDefinition.securitiesFilter
      }

    case 'securities.sortValidationsToTopAction':
      return {
        ...gridDefinition,
        sortToTop: action.payload.sortToTop
      }

    case 'securities.resetSortToTop':
      return {
        ...gridDefinition,
        sortToTop: []
      }

    case 'securities.setQueryFilter':
      return {
        ...gridDefinition,
        issuerFilter: action.payload.query
          ? undefined
          : gridDefinition.issuerFilter,
        queryFilter: action.payload.query,
        securitiesFilter: action.payload.query
          ? undefined
          : gridDefinition.securitiesFilter
      }

    case 'securities.setSecuritiesFilter':
      return {
        ...gridDefinition,
        issuerFilter: action.payload.securityIds
          ? undefined
          : gridDefinition.issuerFilter,
        queryFilter: undefined,
        securitiesFilter: action.payload.securityIds
      }

    case 'securities.setIsMine':
      return {
        ...gridDefinition,
        isMine: action.payload.isMine,
        securitiesFilter: undefined
      }

    case 'securities.setUseAdvancedFilter':
      return {
        ...gridDefinition,
        useAdvancedFilter: action.payload.useAdvancedFilter,
        securitiesFilter: undefined
      }

    case 'securities.setShowLive':
      return {
        ...gridDefinition,
        showLive: action.payload.showLive,
        securitiesFilter: undefined
      }
    case 'securities.setShowRfqEligible':
      return {
        ...gridDefinition,
        showRfqEligible: action.payload.isRFQEligible
      }
    case 'securities.setQuoteReliability':
      return {
        ...gridDefinition,
        quoteReliability: action.payload.quoteReliability,
        securitiesFilter: undefined
      }

    case 'securities.applyQuickFilters':
      return {
        ...gridDefinition,
        myFirm: action.payload.myFirm,
        myBook: action.payload.myBook,
        size: action.payload.size,
        securitiesFilter: undefined
      }

    case 'securities.saveAdvancedFilter':
      return {
        ...gridDefinition,
        filter: action.payload.filter,
        securitiesFilter: undefined
      }

    case 'securities.showSecurityDetails':
      return {
        ...gridDefinition,
        selectedSecurityId: action.payload.securityId
      }

    default:
      return gridDefinition
  }
}

const gridDefinitionsReducer = (
  gridDefinitions = initialGridDefinitions,
  action: GridSpecificAction
): State['gridDefinitions'] => {
  const gridIndex = action.payload?.gridIndex
  if (
    // tslint:disable-next-line: strict-type-predicates
    gridIndex === undefined ||
    !gridDefinitions[gridIndex]
  ) {
    return gridDefinitions
  }
  const updated = gridDefinitionsMiniReducer(gridDefinitions[gridIndex], action)
  if (updated === gridDefinitions[gridIndex]) {
    return gridDefinitions
  }
  return updateGridDataWithState(gridDefinitions, gridIndex, updated)
}
/*
    This is not called on initial load. Instead, the first grid is injected in loadWindowSettings.
 */
const gridCreationReducer = (state: State, action: SecuritiesAction): State => {
  switch (action.type) {
    case 'securities.createGrid':
      const { watchlistId, afterIndex, isMineGrid } = action.payload
      return {
        ...state,
        gridDefinitions: insertGridDef(
          state.gridDefinitions,
          {
            watchlistId,
            isMine: isMineGrid,
            showLive: false,
            showRfqEligible: false,
            quoteReliability: QuoteReliability.All,
            sortToTop: [],
            myFirm: false,
            myBook: false,
            filter: defaultFilter,
            size: 0,
            useAdvancedFilter: false
          },
          afterIndex
        )
      }
    case 'securities.removeGrid':
      return {
        ...state,
        gridDefinitions: removeGridDef(
          state.gridDefinitions,
          action.payload.gridIndex
        )
      }
    case 'securities.unsubscribeGetSecurities':
      return {
        ...state
      }
    default:
      return state
  }
}

const securitiesOrderDataInfoCacheReducer = (
  securitiesInfoCache = initialSecuritiesOrderDataInfoCache,
  action: SecuritiesAction
): State['securitiesOrderDataInfoCache'] => {
  switch (action.type) {
    case 'securities.addOrUpdateSecurities':
    case 'securities.addOrUpdateListTradingSecurities':
      const newSecuritiesInfoCache = { ...securitiesInfoCache }
      for (const security of action.payload.securities) {
        newSecuritiesInfoCache[security.id] =
          securityOrderDataFromSecurity(security)
      }
      return newSecuritiesInfoCache
    case 'securities.addOrUpdateSecuritiesCache':
      const newSecuritiesInfoCache2 = { ...securitiesInfoCache }
      for (const security of action.payload.securities) {
        newSecuritiesInfoCache2[security.id] =
          securityOrderDataFromSecurity(security)
      }
      return newSecuritiesInfoCache2
    case 'securities.unsubscribeGetSecurities':
    case 'securities.unsubscribeListTrading':
      return {}
    default:
      return securitiesInfoCache
  }
}
const securities7ChordDataInfoCacheReducer = (
  securities7ChordInfoCache = initialSecurities7ChordDataInfoCache,
  action: SecuritiesAction
): State['securities7ChordDataInfoCache'] => {
  switch (action.type) {
    case 'securities.addOrUpdateSecurities':
    case 'securities.addOrUpdateSecuritiesCache':
      const newSecurities7ChordInfoCache = { ...securities7ChordInfoCache }
      for (const security of action.payload.securities) {
        if (security.admin7Chord) {
          newSecurities7ChordInfoCache[security.id] = {
            ...security.admin7Chord,
            id: security.id
          }
        }
      }
      return newSecurities7ChordInfoCache
    case 'securities.unsubscribeGetSecurities':
      return {}
    default:
      return securities7ChordInfoCache
  }
}

const patchRatingsByTicker = (
  cache: State['securitiesStaticDataInfoCache'],
  ticker: string,
  sAndPRating: string
): State['securitiesStaticDataInfoCache'] => {
  const target = ticker.toUpperCase()
  let changed = false
  const next = { ...cache }
  for (const [id, sec] of Object.entries(next)) {
    if (sec.issuerSymbol?.toUpperCase() === target) {
      next[Number(id)] = { ...sec, sAndPRating }
      changed = true
    }
  }
  return changed ? next : cache
}

const securitiesStaticDataInfoCacheReducer = (
  securitiesInfoCache = initialSecuritiesStaticDataInfoCache,
  action: SecuritiesAction | AddOrUpdateListTradingSecuritiesAction
): State['securitiesStaticDataInfoCache'] => {
  switch (action.type) {
    case 'securities.addOrUpdateSecurities':
    case 'securities.addOrUpdateListTradingSecurities':
      return ReduceSecurityStaticData(
        securitiesInfoCache,
        action.payload.securities
      )
    case 'securities.addOrUpdateSecuritiesCache':
      return ReduceSecurityStaticData(
        securitiesInfoCache,
        action.payload.securities
      )
    case 'securities.patchTickerRatings':
      return patchRatingsByTicker(
        securitiesInfoCache,
        action.payload.ticker,
        action.payload.sAndPRating
      )
    /* case 'securities.unsubscribeGetSecurities':
      return {}*/
    default:
      return securitiesInfoCache
  }
}

const defaultSecurityExportState = {
  callState: {
    pending: false,
    error: false
  },
  securities: []
}

const securityExportDataReducer = (
  state: State['securityExportData'] = defaultSecurityExportState,
  action: SecuritiesAction
) => {
  switch (action.type) {
    case 'securities.fetchExportSecurities':
      const { gridIndex, includeMakerName } = action.payload
      return {
        ...state,
        gridIndex,
        includeMakerName,
        callState: { pending: true, error: false },
        securities: []
      }
    case 'securities.fetchExportSecuritiesSucceeded':
      return {
        ...state,
        callState: { pending: false, error: false },
        securities: action.payload.securities
      }
    case 'securities.fetchExportSecuritiesFailed':
      return {
        ...state,
        callState: { pending: false, error: true },
        securities: []
      }
  }
  return state
}

const defaultLoadState: State['allSecuritiesLoadState'] = {
  ...callSucceeded,
  loaded: false
}

const allSecuritiesLoadStateReducer = (
  state = defaultLoadState,
  action: SecuritiesAction
): State['allSecuritiesLoadState'] => {
  switch (action.type) {
    case 'securities.getAllSecurities':
      return { ...callInitiated, loaded: false }
    case 'securities.getAllSecuritiesSucceeded':
      return { ...callSucceeded, loaded: true }
    case 'securities.getAllSecuritiesFailed':
      return { ...callFailed, loaded: false }
  }
  return state
}

export default composeReducers(
  combineReducers({
    gridViewState: gridViewStateReducer,
    gridDefinitions: gridDefinitionsReducer,
    securitiesOrderDataInfoCache: securitiesOrderDataInfoCacheReducer,
    securitiesStaticDataInfoCache: securitiesStaticDataInfoCacheReducer,
    listTradingSecurities: listTradingReducer,
    securities7ChordDataInfoCache: securities7ChordDataInfoCacheReducer,
    securityExportData: securityExportDataReducer,
    allSecuritiesLoadState: allSecuritiesLoadStateReducer
  }),
  gridCreationReducer
)
